import React, { createContext, useContext, useCallback } from "react"
import { useQuery } from "react-query"
import { apiV2 } from "../../api/axiosConfig"


export type Module = "siren" | "incident_vicinity_alerts" | "airlineRiskMatrix"

type ClientModulesContextType = {
    loading: boolean
    modules: Module[]
    reload: () => void
    hasModule: (module: Module) => boolean
}

const ClientModulesContext = createContext<ClientModulesContextType | null>(null)

export function ClientModulesProvider({ children }: { children: React.ReactNode }) {
    const {
        data: modules = [],
        refetch: reload,
        isLoading,
        isFetching,
    } = useQuery<Module[]>(
        ["client-modules"],
        async () => {
            const { data } = await apiV2.get<Module[]>("session/client-modules")
            return data || []
        },
        {
            refetchInterval: 300_000, // reload every 5 minutes
            refetchOnWindowFocus: true,
            staleTime: 299_000,
        }
    )

    const hasModule = useCallback(
        (module: Module) => modules.includes(module),
        [modules]
    )

    if (isLoading) return null

    return (
        <ClientModulesContext.Provider
            value={{
                loading: isFetching,
                modules,
                reload,
                hasModule,
            }}
        >
            {children}
        </ClientModulesContext.Provider>
    )
}

export function useClientModules() {
    const ctx = useContext(ClientModulesContext)
    if (!ctx) throw new Error("useClientModules must be used within ClientModulesProvider")
    return ctx
}
